import type { ChatAttachment } from '@/lib/chatAttachments';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRegisteredFeatures } from '@/contexts/FeatureRegistryContext';
import { findRouteMeta } from '@/lib/routeRegistry';
import { streamChat, type BuyRequestProposal, type DraftProposal, type ImportProposal, type MemoryForgetProposal, type ToolStatus } from './streamChat';
import { MAX_ATTACHMENTS } from '@/lib/chatAttachments';

export interface AskThread {
  id: string;
  title: string;
  updated_at: string;
}

export interface AskMessage {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  /** In-memory only: the import offer and the file it would send. */
  importProposal?: ImportProposal & { file?: File };
  /** In-memory only: a one-listing draft offer and its COA file, if any. */
  draftProposal?: DraftProposal & { coaFile?: File };
  /** A Post a buy request offer. */
  buyRequestProposal?: BuyRequestProposal;
  /** An offer to remove entries from this user's saved memory. */
  memoryForgetProposal?: MemoryForgetProposal;
  /** In-memory only: facts Ask Cannafax saved to memory after this answer. */
  savedMemories?: SavedMemory[];
}

export interface SavedMemory { id: string; kind: string; content: string }

/** Conversations shown per page in history lists. */
export const THREAD_PAGE = 20;

export function useAskCannafaxChat(isOpen: boolean) {
  const { session } = useAuth();
  const location = useLocation();
  const features = useRegisteredFeatures();

  const [threads, setThreads] = useState<AskThread[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AskMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toolStatus, setToolStatus] = useState<ToolStatus | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const lastPromptRef = useRef<string>('');
  const lastAttsRef = useRef<ChatAttachment[]>([]);
  // Refs mirror state so calls made in the same tick (stop → send, new → send)
  // never read a stale thread id or streaming flag from a render closure.
  const threadRef = useRef<string | null>(null);
  const streamingRef = useRef(false);
  // Only the latest openThread may write messages; earlier loads are discarded.
  const openSeq = useRef(0);
  // Files attached earlier in this open chat, re-sent so follow-ups can still use them.
  const carriedRef = useRef<ChatAttachment[]>([]);

  const setThread = (id: string | null) => { if (id !== threadRef.current && threadRef.current !== null) carriedRef.current = []; threadRef.current = id; setActiveThreadId(id); };
  const setStreaming = (v: boolean) => { streamingRef.current = v; setIsStreaming(v); if (!v) setToolStatus(null); };

  // History pages in steps of THREAD_PAGE; refreshes keep however many pages are already shown.
  const [threadLimit, setThreadLimit] = useState(THREAD_PAGE);
  const [hasMoreThreads, setHasMoreThreads] = useState(false);
  const limitRef = useRef(THREAD_PAGE);
  const loadThreads = useCallback(async () => {
    setLoadingThreads(true);
    const lim = limitRef.current;
    const { data } = await supabase
      .from('help_threads' as any)
      .select('id, title, updated_at')
      .order('updated_at', { ascending: false })
      .limit(lim + 1);
    const rows = (data as any as AskThread[]) || [];
    setHasMoreThreads(rows.length > lim);
    setThreads(rows.slice(0, lim));
    setLoadingThreads(false);
  }, []);
  const loadMoreThreads = useCallback(() => {
    limitRef.current += THREAD_PAGE;
    setThreadLimit(limitRef.current);
  }, []);

  useEffect(() => {
    if (isOpen) loadThreads();
  }, [isOpen, loadThreads, threadLimit]);

  const halt = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
  };

  /** Loads a saved conversation. Resolves to null if a newer open/new chat superseded it. */
  const openThread = useCallback(async (threadId: string): Promise<AskMessage[] | null> => {
    halt();
    const seq = ++openSeq.current;
    setThread(threadId);
    setMessages([]);
    setError(null);
    setLoadingMessages(true);
    const { data } = await supabase
      .from('help_messages' as any)
      .select('id, role, content, cards')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true })
      .limit(50);
    if (seq !== openSeq.current) return null;
    // Saved action cards come back in place (without the original file) so the chronology stays whole.
    const rows: AskMessage[] = ((data as any[]) || []).map(({ cards, ...m }) => ({
      ...m,
      ...(cards?.draft ? { draftProposal: cards.draft } : {}),
      ...(cards?.buyRequest ? { buyRequestProposal: cards.buyRequest } : {}),
      ...(cards?.import ? { importProposal: cards.import } : {}),
      ...(cards?.memoryForget ? { memoryForgetProposal: cards.memoryForget } : {}),
    }));
    setMessages(rows);
    setLoadingMessages(false);
    return rows;
  }, []);

  const startNewChat = useCallback(() => {
    halt();
    openSeq.current++;
    setThread(null);
    carriedRef.current = [];
    setMessages([]);
    setError(null);
    setLoadingMessages(false);
  }, []);

  const deleteThread = useCallback(
    async (threadId: string) => {
      await supabase.from('help_threads' as any).delete().eq('id', threadId);
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
      if (threadRef.current === threadId) startNewChat();
    },
    [startNewChat],
  );

  const stop = useCallback(() => halt(), []);

  // Memory is saved in the background after the answer finishes. Watch briefly for this
  // user's own new auto-saved facts and attach them to the answer as a "Remembered" note.
  const shownMemoryIds = useRef<Set<string>>(new Set());
  const watchSavedMemories = useCallback(async (seq: number, since: string) => {
    const uid = session?.user?.id;
    if (!uid) return;
    let found: SavedMemory[] = [];
    for (let attempt = 0; attempt < 10; attempt++) {
      await new Promise((r) => setTimeout(r, attempt === 0 ? 2_500 : 2_000));
      if (seq !== openSeq.current) return;
      const { data } = await supabase
        .from('desk_memories')
        .select('id, kind, content')
        .eq('user_id', uid)
        .eq('source', 'auto')
        .eq('status', 'active')
        .gte('created_at', since)
        .order('created_at', { ascending: true })
        .limit(8);
      const fresh = ((data ?? []) as SavedMemory[]).filter((m) => !shownMemoryIds.current.has(m.id));
      // Stop once results settle (one extra look after the first hit).
      if (found.length && fresh.length === found.length) break;
      found = fresh;
    }
    if (!found.length || seq !== openSeq.current) return;
    found.forEach((m) => shownMemoryIds.current.add(m.id));
    setMessages((prev) => {
      const idx = prev.map((m) => m.role).lastIndexOf('assistant');
      if (idx < 0) return prev;
      return prev.map((m, i) => (i === idx ? { ...m, savedMemories: [...(m.savedMemories ?? []), ...found] } : m));
    });
  }, [session?.user?.id]);

  /**
   * Sends a message. `newThread` always starts a separate conversation (never appends
   * to the current one); `onThreadId` fires as soon as the server assigns the thread.
   */
  const send = useCallback(
    async (text: string, opts: { newThread?: boolean; onThreadId?: (id: string) => void; deskMoveKey?: string | null; attachments?: ChatAttachment[] } = {}) => {
      const trimmed = text.trim();
      if (!trimmed || !session?.access_token) return;
      if (streamingRef.current && !opts.newThread) return;
      if (opts.newThread) startNewChat();

      lastPromptRef.current = trimmed;
      lastAttsRef.current = opts.attachments ?? [];
      setError(null);
      const atts = opts.attachments ?? [];
      const carried = carriedRef.current.filter((c) => !atts.some((a) => a.name === c.name));
      const allAtts = [...atts, ...carried].slice(0, MAX_ATTACHMENTS);
      carriedRef.current = allAtts;
      const shown = atts.length ? `${trimmed}\n\n📎 Attached: ${atts.map((a) => a.name).join(', ')}` : trimmed;
      setMessages((prev) => [...prev, { role: 'user', content: shown }]);
      setStreaming(true);
      // Server clocks can lead the browser slightly; already-shown ids are skipped.
      const turnStartedAt = new Date(Date.now() - 5_000).toISOString();

      const controller = new AbortController();
      abortRef.current = controller;
      const seq = openSeq.current;
      const current = () => seq === openSeq.current && abortRef.current === controller;

      let assistantSoFar = '';
      const upsertAssistant = (chunk: string) => {
        if (!current()) return;
        setToolStatus(null);
        assistantSoFar += chunk;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant' && !last.id) {
            return prev.map((m, i) =>
              i === prev.length - 1 ? { ...m, content: assistantSoFar } : m,
            );
          }
          return [...prev, { role: 'assistant', content: assistantSoFar }];
        });
      };

      const routeMeta = findRouteMeta(location.pathname);
      const pageContext = {
        routeMeta: routeMeta || undefined,
        // Your Desk: only the key of the card on screen; the server recomputes its facts.
        deskMoveKey: opts.deskMoveKey ?? undefined,
        features: features.map((f) => ({
          id: f.id,
          label: f.label,
          description: f.description,
          triggerType: f.triggerType,
        })),
      };

      await streamChat({
        message: trimmed,
        threadId: threadRef.current || undefined,
        pageContext,
        attachments: allAtts.length
          ? allAtts.map(({ name, text, truncated }) => ({ name, text, truncated, ...(atts.some((a) => a.name === name) ? {} : { earlier: true }) }))
          : undefined,
        token: session.access_token,
        signal: controller.signal,
        onDelta: upsertAssistant,
        onStatus: (st) => { if (current()) setToolStatus(st); },
        onImportProposal: (p) => {
          if (!current()) return;
          const file = allAtts.find((a) => a.name === p.fileName)?.file;
          if (!file) return;
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.id) {
              return prev.map((m, i) => (i === prev.length - 1 ? { ...m, importProposal: { ...p, file } } : m));
            }
            return [...prev, { role: 'assistant', content: '', importProposal: { ...p, file } }];
          });
        },
        onDraftProposal: (p) => {
          if (!current()) return;
          const coaFile = p.coaFileName ? allAtts.find((a) => a.name === p.coaFileName)?.file : undefined;
          const draftProposal = { ...p, coaFileName: coaFile ? p.coaFileName : null, coaFile };
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.id) {
              return prev.map((m, i) => (i === prev.length - 1 ? { ...m, draftProposal } : m));
            }
            return [...prev, { role: 'assistant', content: '', draftProposal }];
          });
        },
        onBuyRequestProposal: (buyRequestProposal) => {
          if (!current()) return;
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.id) {
              return prev.map((m, i) => (i === prev.length - 1 ? { ...m, buyRequestProposal } : m));
            }
            return [...prev, { role: 'assistant', content: '', buyRequestProposal }];
          });
        },
        onMemoryForgetProposal: (memoryForgetProposal) => {
          if (!current()) return;
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.id) {
              return prev.map((m, i) => (i === prev.length - 1 ? { ...m, memoryForgetProposal } : m));
            }
            return [...prev, { role: 'assistant', content: '', memoryForgetProposal }];
          });
        },
        onDone: () => {
          if (!current()) return;
          setStreaming(false);
          void watchSavedMemories(seq, turnStartedAt);
        },
        onThreadId: (id) => {
          opts.onThreadId?.(id);
          if (current()) setThread(id);
          loadThreads();
        },
        onError: (err) => {
          if (!current()) return;
          setError(err);
          setStreaming(false);
        },
      });
    },
    [session, location.pathname, features, loadThreads, startNewChat, watchSavedMemories],
  );

  const retry = useCallback(() => {
    const prompt = lastPromptRef.current;
    if (!prompt) return;
    setError(null);
    // Drop the trailing user message so send() re-adds it once.
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      return last?.role === 'user' && last.content.startsWith(prompt) ? prev.slice(0, -1) : prev;
    });
    void send(prompt, { attachments: lastAttsRef.current });
  }, [send]);

  useEffect(() => () => abortRef.current?.abort(), []);

  return {
    threads,
    loadingThreads,
    hasMoreThreads,
    loadMoreThreads,
    activeThreadId,
    messages,
    loadingMessages,
    isStreaming,
    toolStatus,
    error,
    send,
    retry,
    stop,
    openThread,
    startNewChat,
    deleteThread,
  };
}